import React, { useEffect, useMemo, useState } from 'react';
import {
  AlertCircle,
  AlertTriangle,
  ArrowLeft,
  CheckCircle2,
  ChevronRight,
  EyeOff,
  FileSpreadsheet,
  Package,
  Tag,
  UploadCloud,
  X,
  XCircle,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { supabase } from '../supabase';
import { PRICE_LIST_MODE_FIXED } from '../utils/priceLists';

const MAX_IMPORT_FILE_SIZE_MB = 8;

const PRICE_LIST_IMPORT_FIELDS = [
  {
    key: 'product_name',
    label: 'Nombre del Producto',
    required: false,
    aliases: ['producto', 'articulo', 'item', 'descripcion', 'nombre'],
  },
  {
    key: 'barcode',
    label: 'Codigo / Barras',
    required: false,
    aliases: ['codigo', 'codigo barras', 'codigo de barras', 'barcode', 'ean', 'gtin', 'sku'],
  },
  {
    key: 'price',
    label: 'Precio Lista',
    required: true,
    type: 'number',
    aliases: ['precio', 'precio lista', 'precio venta', 'valor', 'importe', 'monto'],
  },
];

const normalize = (value) =>
  String(value ?? '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '');

const parseNumberValue = (value) => {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;

  const raw = String(value).trim();
  if (!raw) return null;

  let normalizedValue = raw.replace(/[$\s]/g, '');
  if (normalizedValue.includes(',') && normalizedValue.includes('.')) {
    normalizedValue = normalizedValue.replace(/\./g, '').replace(',', '.');
  } else if (normalizedValue.includes(',')) {
    normalizedValue = normalizedValue.replace(',', '.');
  }

  const parsed = Number.parseFloat(normalizedValue);
  return Number.isFinite(parsed) ? parsed : null;
};

const buildInitialMap = (fileHeaders, fields) => {
  const initialMap = {};

  fields.forEach((field) => {
    const match = fileHeaders.find((header) => {
      const normalizedHeader = normalize(header);
      return (
        normalizedHeader === normalize(field.key) ||
        normalizedHeader === normalize(field.label) ||
        normalizedHeader.includes(normalize(field.label)) ||
        (field.aliases || []).some(
          (alias) =>
            normalizedHeader === normalize(alias) || normalizedHeader.includes(normalize(alias))
        )
      );
    });

    if (match) initialMap[field.key] = match;
  });

  return initialMap;
};

const isPdfFile = (file) =>
  String(file?.type || '').includes('pdf') || String(file?.name || '').toLowerCase().endsWith('.pdf');

const isExcelFile = (file) => {
  const name = String(file?.name || '').toLowerCase();
  return name.endsWith('.xlsx') || name.endsWith('.xls');
};

const getFriendlyImportError = (error) => {
  const message = String(error?.message || error || '').trim();
  if (!message) return 'Ocurrio un error inesperado.';

  const normalizedMessage = normalize(message);

  if (normalizedMessage.includes('network')) return 'Se corto la conexion mientras se procesaba la importacion.';
  if (normalizedMessage.includes('json valido')) return 'La lectura automatica del archivo no devolvio filas utilizables.';

  return message;
};

const getStatusMeta = (status) => {
  if (status === 'success') {
    return {
      icon: <CheckCircle2 size={16} className="text-emerald-500" />,
      badge: 'bg-emerald-100 text-emerald-700',
      card: 'border-emerald-100 bg-emerald-50/50',
      label: 'OK',
    };
  }

  if (status === 'skipped') {
    return {
      icon: <AlertTriangle size={16} className="text-amber-500" />,
      badge: 'bg-amber-100 text-amber-700',
      card: 'border-amber-100 bg-amber-50/50',
      label: 'OMITIDA',
    };
  }

  return {
    icon: <XCircle size={16} className="text-rose-500" />,
    badge: 'bg-rose-100 text-rose-700',
    card: 'border-rose-100 bg-rose-50/50',
    label: 'ERROR',
  };
};

/**
 * PriceListImportModal
 *
 * Props:
 *   open            - boolean
 *   onClose         - fn()
 *   products        - array de productos del catálogo
 *   currentPrices   - { [productId]: price } (modo apply)
 *   onApplyImport   - fn(nextPrices, successfulLogs) (modo apply)
 *   priceListName   - string (modo apply)
 *   mode            - 'apply' | 'create'  (default: 'apply')
 *   orgId           - string (requerido en modo create)
 *   onListCreated   - fn() callback tras crear la lista (modo create)
 */
function PriceListImportModal({
  open,
  onClose,
  products = [],
  currentPrices = {},
  onApplyImport,
  priceListName = '',
  mode = 'apply',
  orgId,
  onListCreated,
}) {
  const isCreateMode = mode === 'create';

  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [progress, setProgress] = useState('');
  const [fileName, setFileName] = useState('');
  const [headers, setHeaders] = useState([]);
  const [rawRows, setRawRows] = useState([]);
  const [colMapping, setColMapping] = useState({});
  const [importLogs, setImportLogs] = useState([]);
  const [logFilter, setLogFilter] = useState('all');
  const [previewPrices, setPreviewPrices] = useState(null);

  // Create mode: nombre de la nueva lista
  const [newListName, setNewListName] = useState('');

  useEffect(() => {
    if (!open) {
      setStep(1);
      setLoading(false);
      setCreating(false);
      setProgress('');
      setFileName('');
      setHeaders([]);
      setRawRows([]);
      setColMapping({});
      setImportLogs([]);
      setLogFilter('all');
      setPreviewPrices(null);
      setNewListName('');
    }
  }, [open]);

  const productIndexes = useMemo(() => {
    const byBarcode = new Map();
    const byName = new Map();

    products.forEach((product) => {
      const normalizedName = normalize(product?.name);
      const normalizedBarcode = normalize(product?.barcode);

      if (normalizedName) byName.set(normalizedName, product);
      if (normalizedBarcode) byBarcode.set(normalizedBarcode, product);
    });

    return { byBarcode, byName };
  }, [products]);

  const stats = useMemo(
    () =>
      importLogs.reduce(
        (acc, log) => {
          if (log.status === 'success') acc.success += 1;
          else if (log.status === 'error') acc.error += 1;
          else if (log.status === 'skipped') acc.skipped += 1;
          return acc;
        },
        { success: 0, error: 0, skipped: 0 }
      ),
    [importLogs]
  );

  const filteredLogs = useMemo(
    () => importLogs.filter((log) => logFilter === 'all' || log.status === logFilter),
    [importLogs, logFilter]
  );

  const importedProductCount = useMemo(() => {
    const successfulProducts = importLogs
      .filter((log) => log.status === 'success' && log.productId)
      .map((log) => log.productId);

    return new Set(successfulProducts).size;
  }, [importLogs]);

  // Cantidad de productos del catálogo que NO aparecen en el archivo (quedarán ocultos en create mode)
  const hiddenProductCount = useMemo(() => {
    if (!previewPrices || !products.length) return 0;
    const matchedIds = new Set(Object.keys(previewPrices).map(String));
    return products.filter((p) => !matchedIds.has(String(p.id))).length;
  }, [previewPrices, products]);

  const hydrateImportedData = ({ importedRows, importedHeaders, sourceName, initialMap = {} }) => {
    setFileName(sourceName || '');
    setHeaders(importedHeaders || []);
    setRawRows(importedRows || []);
    setColMapping(initialMap);
    setImportLogs([]);
    setPreviewPrices(null);
    setLogFilter('all');
    setStep(2);
  };

  const buildDraftFromRawRow = (row) => {
    const draft = {};
    PRICE_LIST_IMPORT_FIELDS.forEach((field) => {
      const header = colMapping[field.key];
      draft[field.key] = header ? row?.[header] ?? '' : '';
    });
    return draft;
  };

  const handleClose = () => {
    if (loading || creating) return;
    setStep(1);
    setLoading(false);
    setCreating(false);
    setProgress('');
    setFileName('');
    setHeaders([]);
    setRawRows([]);
    setColMapping({});
    setImportLogs([]);
    setLogFilter('all');
    setPreviewPrices(null);
    setNewListName('');
    onClose?.();
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (isPdfFile(file)) {
      setFileName(file.name);
      setLoading(true);

      try {
        if (file.size > MAX_IMPORT_FILE_SIZE_MB * 1024 * 1024) {
          throw new Error(`El PDF es muy pesado. Usa uno menor a ${MAX_IMPORT_FILE_SIZE_MB}MB.`);
        }

        const fileData = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.onerror = () => reject(new Error('No se pudo leer el PDF.'));
          reader.readAsDataURL(file);
        });

        const { data: sessionData } = await supabase.auth.getSession();
        const accessToken = sessionData?.session?.access_token;
        if (!accessToken) throw new Error('No hay sesion activa. Recarga la pagina.');

        const { data, error } = await supabase.functions.invoke('ocr-importaciones', {
          body: {
            fileData,
            fileName: file.name,
            fileType: file.type || 'application/pdf',
            importType: 'PRICE_LISTS',
            importLabel: 'Lista de precios',
            fields: PRICE_LIST_IMPORT_FIELDS.map((field) => ({
              key: field.key,
              label: field.label,
              type: field.type || 'string',
              required: Boolean(field.required),
            })),
          },
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        });

        if (error) throw new Error(error.message || 'Error al conectar con el lector PDF.');
        if (data?.ok === false) throw new Error(data.error || 'No se pudo leer el PDF.');

        const structuredRows = Array.isArray(data?.data?.rows) ? data.data.rows : [];
        const importedHeaders = PRICE_LIST_IMPORT_FIELDS.map((field) => field.label);
        const importedRows = structuredRows
          .map((row) => {
            const nextRow = {};
            PRICE_LIST_IMPORT_FIELDS.forEach((field) => {
              nextRow[field.label] = row?.[field.key] ?? '';
            });
            return nextRow;
          })
          .filter((row) => Object.values(row).some((value) => String(value ?? '').trim() !== ''));

        if (!importedRows.length) {
          throw new Error('El PDF no devolvio filas utilizables para importar.');
        }

        const initialMap = {};
        PRICE_LIST_IMPORT_FIELDS.forEach((field) => {
          initialMap[field.key] = field.label;
        });

        hydrateImportedData({
          importedRows,
          importedHeaders,
          sourceName: file.name,
          initialMap,
        });

        toast.success('PDF procesado. Revisa el mapeo antes de aplicar.');
      } catch (error) {
        console.error(error);
        toast.error(getFriendlyImportError(error));
      } finally {
        setLoading(false);
        if (event.target) event.target.value = '';
      }

      return;
    }

    if (!isExcelFile(file)) {
      toast.error('Formato no soportado. Usa Excel o PDF.');
      if (event.target) event.target.value = '';
      return;
    }

    let XLSX;
    try {
      const module = await import('xlsx');
      XLSX = module?.default || module;
    } catch (_error) {
      toast.error('No se pudo cargar la libreria XLSX.');
      if (event.target) event.target.value = '';
      return;
    }

    setFileName(file.name);
    setLoading(true);

    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data, { type: 'array' });
      const sheetName = workbook.SheetNames[0];
      const sheet = workbook.Sheets[sheetName];
      const jsonData = XLSX.utils.sheet_to_json(sheet, { defval: '' });

      if (!jsonData?.length) throw new Error('El archivo esta vacio.');

      const fileHeaders = Object.keys(jsonData[0] || {});
      hydrateImportedData({
        importedRows: jsonData,
        importedHeaders: fileHeaders,
        sourceName: file.name,
        initialMap: buildInitialMap(fileHeaders, PRICE_LIST_IMPORT_FIELDS),
      });
    } catch (error) {
      console.error(error);
      toast.error(getFriendlyImportError(error));
    } finally {
      setLoading(false);
      if (event.target) event.target.value = '';
    }
  };

  const executeImport = async () => {
    const hasIdentifierColumn = Boolean(colMapping.product_name || colMapping.barcode);
    if (!colMapping.price) {
      toast.error('Mapea la columna del precio antes de continuar.');
      return;
    }
    if (!hasIdentifierColumn) {
      toast.error('Mapea al menos una columna de nombre o codigo para identificar productos.');
      return;
    }

    setLoading(true);
    setProgress('Validando filas importadas...');

    try {
      const logs = [];
      const nextPrices = isCreateMode ? {} : { ...(currentPrices || {}) };
      const appliedRowsByProductId = new Map();

      for (let index = 0; index < rawRows.length; index += 1) {
        const row = rawRows[index];
        const rowIndex = index + 1;
        const draft = buildDraftFromRawRow(row);
        const parsedPrice = parseNumberValue(draft.price);
        const normalizedBarcode = normalize(draft.barcode);
        const normalizedName = normalize(draft.product_name);
        const matchedProduct =
          (normalizedBarcode && productIndexes.byBarcode.get(normalizedBarcode)) ||
          (normalizedName && productIndexes.byName.get(normalizedName)) ||
          null;

        setProgress(`Procesando fila ${rowIndex}/${rawRows.length}...`);

        if (parsedPrice === null || parsedPrice < 0) {
          logs.push({
            row: rowIndex,
            status: 'error',
            msg: 'El precio no es valido.',
            draft,
          });
          continue;
        }

        if (!matchedProduct) {
          logs.push({
            row: rowIndex,
            status: 'error',
            msg: 'No se encontro un producto del catalogo con ese nombre o codigo.',
            draft,
          });
          continue;
        }

        const previousRow = appliedRowsByProductId.get(matchedProduct.id);
        appliedRowsByProductId.set(matchedProduct.id, rowIndex);
        nextPrices[matchedProduct.id] = parsedPrice;

        logs.push({
          row: rowIndex,
          status: 'success',
          msg: previousRow
            ? `Precio preparado para ${matchedProduct.name}. Reemplaza la fila ${previousRow}.`
            : `Precio preparado para ${matchedProduct.name}.`,
          draft,
          productId: matchedProduct.id,
          productName: matchedProduct.name,
          price: parsedPrice,
        });
      }

      setImportLogs(logs);
      setPreviewPrices(nextPrices);
      setStep(4);
      toast.success('Importacion procesada. Revisa el resultado antes de aplicar.');
    } catch (error) {
      console.error(error);
      toast.error(getFriendlyImportError(error));
    } finally {
      setLoading(false);
      setProgress('');
    }
  };

  // Modo apply: aplica los precios al borrador del formulario (comportamiento original)
  const handleApplyImport = () => {
    const successfulLogs = importLogs.filter((log) => log.status === 'success');
    if (!previewPrices || !successfulLogs.length) {
      toast.error('No hay precios validos para aplicar.');
      return;
    }

    onApplyImport?.(previewPrices, successfulLogs);
    handleClose();
  };

  // Modo create: crea una nueva lista en Supabase con precios importados y productos no incluidos como ocultos
  const handleCreateList = async () => {
    if (!newListName.trim()) {
      toast.error('Completa el nombre de la lista antes de guardar.');
      return;
    }
    if (!stats.success) {
      toast.error('No hay precios validos para crear la lista.');
      return;
    }
    if (!orgId) {
      toast.error('Error: falta la organizacion. Recarga la pagina.');
      return;
    }

    setCreating(true);
    try {
      // Productos del catálogo que NO figuran en el archivo → quedan ocultos
      const matchedIds = new Set(Object.keys(previewPrices || {}).map(String));
      const hiddenProductIds = products
        .filter((p) => !matchedIds.has(String(p.id)))
        .map((p) => String(p.id));

      const { error } = await supabase.from('price_lists').insert([
        {
          organization_id: orgId,
          name: newListName.trim().toUpperCase(),
          pricing_mode: PRICE_LIST_MODE_FIXED,
          adjustment_percentage: 0,
          product_prices: previewPrices,
          hidden_product_ids: hiddenProductIds,
          is_active: true,
        },
      ]);

      if (error) throw error;

      toast.success(
        `Lista "${newListName.trim().toUpperCase()}" creada — ${importedProductCount} precios cargados, ${hiddenProductIds.length} productos ocultos.`
      );
      onListCreated?.();
      handleClose();
    } catch (error) {
      console.error(error);
      toast.error(error?.message || 'Error al crear la lista.');
    } finally {
      setCreating(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/55 backdrop-blur-sm p-4">
      <div className="w-full max-w-6xl max-h-[92vh] overflow-hidden rounded-[28px] border border-slate-200 bg-[#F2F9FF] shadow-2xl flex flex-col">
        {/* Header */}
        <div className="bg-white rounded-t-[28px] border-b border-slate-200 px-5 py-4 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={handleClose}
              disabled={loading || creating}
              className="w-10 h-10 rounded-xl bg-slate-50 hover:bg-slate-100 flex items-center justify-center text-slate-500 transition-colors disabled:opacity-40"
              title="Cerrar"
            >
              <X size={18} />
            </button>
            <div className="w-11 h-11 rounded-2xl bg-[#0B2A36] flex items-center justify-center shadow-sm">
              <Package size={18} className="text-[#3FF2AA]" />
            </div>
            <div>
              <div className="text-[10px] font-black uppercase tracking-widest text-slate-400">
                {isCreateMode ? 'Crear nueva lista desde archivo' : 'Importador de lista puntual'}
              </div>
              <h2 className="text-lg md:text-xl font-black text-slate-900 uppercase tracking-tight">
                {isCreateMode
                  ? 'Importar lista de precios'
                  : priceListName
                    ? `Lista ${priceListName}`
                    : 'Lista de precios'}
              </h2>
            </div>
          </div>

          {/* Steps */}
          <div className="flex items-center gap-2 bg-slate-50 px-4 py-2 rounded-2xl overflow-x-auto">
            {[
              { n: 1, l: 'Cargar' },
              { n: 2, l: 'Mapear' },
              { n: 3, l: 'Confirmar' },
              { n: 4, l: 'Resultados' },
            ].map((section, index) => (
              <React.Fragment key={section.n}>
                <div
                  className={`flex items-center gap-2 whitespace-nowrap ${
                    step === section.n
                      ? 'text-blue-600'
                      : step > section.n
                        ? 'text-emerald-500'
                        : 'text-slate-300'
                  }`}
                >
                  <div
                    className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-black border ${
                      step === section.n
                        ? 'bg-blue-100 border-blue-200'
                        : step > section.n
                          ? 'bg-emerald-100 border-emerald-200'
                          : 'bg-white border-slate-200'
                    }`}
                  >
                    {step > section.n ? <CheckCircle2 size={14} /> : section.n}
                  </div>
                  <span className="text-[10px] font-black uppercase hidden md:block">{section.l}</span>
                </div>
                {index < 3 && <div className="w-4 h-px bg-slate-200" />}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto">

          {/* STEP 1: UPLOAD */}
          {step === 1 && (
            <div className="p-8 md:p-10 min-h-[560px] flex flex-col items-center justify-center text-center">
              <div className="w-24 h-24 bg-blue-50 rounded-full flex items-center justify-center mb-6 ring-8 ring-blue-50/70">
                <UploadCloud size={40} className="text-blue-500" />
              </div>
              <h3 className="text-xl font-black text-slate-800 uppercase mb-3">
                {isCreateMode ? 'Subí la lista para crear' : 'Subi la lista a importar'}
              </h3>
              <p className="text-sm text-slate-500 max-w-2xl leading-relaxed mb-8">
                {isCreateMode
                  ? 'El sistema va a comparar los productos del archivo con tu catálogo. Los que coincidan tendrán precio específico. Los que no estén en el archivo quedarán ocultos en la nueva lista.'
                  : 'Esta importacion carga precios especificos dentro de esta lista. Acepta planillas'}
                {!isCreateMode && (
                  <>
                    {' '}
                    <strong>.xlsx</strong>, <strong>.xls</strong> o <strong>.pdf</strong>, igual que el
                    centro de importaciones.
                  </>
                )}
                {isCreateMode && (
                  <>
                    {' '}Acepta <strong>.xlsx</strong>, <strong>.xls</strong> o <strong>.pdf</strong>.
                  </>
                )}
              </p>

              <label className="cursor-pointer group relative">
                <input
                  type="file"
                  accept=".xlsx,.xls,.pdf,application/pdf"
                  className="hidden"
                  onChange={handleFileUpload}
                  disabled={loading}
                />
                <div className="px-10 py-5 bg-[#0B2A36] text-white rounded-2xl font-black uppercase tracking-widest hover:bg-slate-800 transition-all shadow-xl shadow-slate-200 group-hover:-translate-y-1 flex items-center gap-3">
                  {loading ? (
                    <div className="animate-spin w-5 h-5 border-2 border-white/30 border-t-white rounded-full" />
                  ) : (
                    <FileSpreadsheet size={20} />
                  )}
                  <span>{loading ? 'Procesando...' : 'Seleccionar Excel o PDF'}</span>
                </div>
              </label>

              <div className="mt-12 max-w-3xl w-full">
                <div className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-4">
                  Campos esperados
                </div>
                <div className="flex flex-wrap justify-center gap-2">
                  {PRICE_LIST_IMPORT_FIELDS.map((field) => (
                    <div
                      key={field.key}
                      className={`px-3 py-1.5 rounded-lg border text-[10px] font-bold uppercase flex items-center gap-1.5 ${
                        field.required
                          ? 'bg-blue-50 text-blue-700 border-blue-200'
                          : 'bg-slate-50 text-slate-500 border-slate-200'
                      }`}
                    >
                      {field.required && <AlertCircle size={10} />}
                      {field.label}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* STEP 2: MAP */}
          {step === 2 && (
            <div className="p-6 md:p-8 animate-fade-in">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
                <div>
                  <h3 className="text-lg font-black text-slate-800 uppercase">Validar columnas</h3>
                  <p className="text-xs text-slate-500">
                    Archivo detectado: <span className="font-black text-slate-700">{fileName || 'Sin nombre'}</span>
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setStep(1)}
                  className="px-4 py-2 rounded-xl border border-slate-200 text-slate-600 font-bold text-xs uppercase hover:bg-slate-50 transition-colors"
                >
                  Cambiar archivo
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {PRICE_LIST_IMPORT_FIELDS.map((field) => {
                  const isMapped = Boolean(colMapping[field.key]);

                  return (
                    <div
                      key={field.key}
                      className={`p-4 rounded-2xl border transition-all ${
                        isMapped ? 'bg-white border-slate-200' : 'bg-rose-50 border-rose-100'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <label className="text-[11px] font-black uppercase text-slate-700">
                            {field.label}
                          </label>
                          {field.required && (
                            <span className="text-[9px] font-black text-white bg-rose-500 px-1.5 py-0.5 rounded">
                              OBLIGATORIO
                            </span>
                          )}
                        </div>
                        {isMapped ? (
                          <CheckCircle2 size={16} className="text-emerald-500" />
                        ) : (
                          <AlertTriangle size={16} className="text-rose-400" />
                        )}
                      </div>

                      <select
                        value={colMapping[field.key] || ''}
                        onChange={(event) =>
                          setColMapping((current) => ({
                            ...current,
                            [field.key]: event.target.value || '',
                          }))
                        }
                        className="w-full bg-slate-50 border border-slate-200 px-3 py-3 rounded-xl text-[11px] font-bold outline-none focus:border-[#51A7FD]"
                      >
                        <option value="">No mapear</option>
                        {headers.map((header) => (
                          <option key={header} value={header}>
                            {header}
                          </option>
                        ))}
                      </select>

                      <div className="mt-3 text-[10px] font-bold text-slate-400">
                        Ejemplo:{' '}
                        <span className="text-slate-600">
                          {rawRows[0] && colMapping[field.key] ? String(rawRows[0][colMapping[field.key]] ?? '') : '-'}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="mt-6 flex flex-col md:flex-row items-center justify-between gap-3">
                <p className="text-[11px] font-bold text-slate-500">
                  Necesitas mapear <span className="font-black text-slate-700">Precio Lista</span> y al
                  menos uno entre <span className="font-black text-slate-700">Nombre</span> o{' '}
                  <span className="font-black text-slate-700">Codigo</span>.
                </p>

                <button
                  type="button"
                  onClick={() => {
                    if (!colMapping.price) {
                      toast.error('Mapea la columna del precio antes de continuar.');
                      return;
                    }
                    if (!colMapping.product_name && !colMapping.barcode) {
                      toast.error('Mapea una columna de nombre o codigo para identificar productos.');
                      return;
                    }
                    setStep(3);
                  }}
                  className="px-6 py-3 rounded-2xl bg-[#0B2A36] text-white font-black text-[10px] uppercase tracking-widest hover:bg-slate-800 transition-all flex items-center gap-2"
                >
                  Revisar importacion
                  <ChevronRight size={16} />
                </button>
              </div>
            </div>
          )}

          {/* STEP 3: PREVIEW */}
          {step === 3 && (
            <div className="p-6 md:p-8 animate-fade-in">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
                <div>
                  <h3 className="text-lg font-black text-slate-800 uppercase">Previsualizacion</h3>
                  <p className="text-xs text-slate-500">
                    Se detectaron <span className="font-black text-slate-700">{rawRows.length}</span> filas
                    para revisar.
                  </p>
                </div>

                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => setStep(2)}
                    className="px-5 py-3 rounded-xl border border-slate-200 text-slate-600 font-bold text-xs uppercase hover:bg-slate-50 transition-colors"
                  >
                    Volver al mapeo
                  </button>
                  <button
                    type="button"
                    onClick={executeImport}
                    disabled={loading}
                    className="px-6 py-3 rounded-2xl bg-[#3FF2AA] text-[#0B2A36] font-black text-[10px] uppercase tracking-widest hover:brightness-95 transition-all shadow-lg disabled:opacity-50"
                  >
                    {loading ? 'Procesando...' : 'Procesar importacion'}
                  </button>
                </div>
              </div>

              <div className="rounded-3xl border border-slate-200 overflow-hidden bg-white">
                <div className="grid grid-cols-3 bg-slate-50 border-b border-slate-200">
                  {PRICE_LIST_IMPORT_FIELDS.map((field) => (
                    <div
                      key={field.key}
                      className="px-4 py-3 text-[10px] font-black uppercase tracking-widest text-slate-500"
                    >
                      {field.label}
                    </div>
                  ))}
                </div>

                <div className="max-h-[420px] overflow-y-auto divide-y divide-slate-100">
                  {rawRows.slice(0, 60).map((row, index) => (
                    <div key={`${index + 1}-${fileName}`} className="grid grid-cols-3">
                      {PRICE_LIST_IMPORT_FIELDS.map((field) => (
                        <div
                          key={field.key}
                          className="px-4 py-3 text-[11px] font-bold text-slate-700 truncate"
                        >
                          {String(row[colMapping[field.key]] ?? '') || '-'}
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              </div>

              {rawRows.length > 60 && (
                <p className="mt-3 text-[10px] font-black uppercase tracking-widest text-slate-400 text-center">
                  ... y {rawRows.length - 60} filas mas
                </p>
              )}
            </div>
          )}

          {/* STEP 4: RESULTS */}
          {step === 4 && (
            <div className="p-6 md:p-8 animate-fade-in">
              <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4 mb-6">
                <div>
                  <h3 className="text-lg font-black text-slate-800 uppercase">Resultados</h3>
                  <p className="text-xs text-slate-500">
                    {isCreateMode
                      ? 'Revisá los precios encontrados y asigná un nombre para crear la lista.'
                      : 'La importacion todavia no se guardo en la lista. Primero revisa y luego aplica los precios al formulario.'}
                  </p>
                </div>

                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => setLogFilter('all')}
                    className={`px-3 py-1 rounded-lg text-[10px] font-black uppercase transition-all ${
                      logFilter === 'all'
                        ? 'bg-slate-800 text-white'
                        : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                    }`}
                  >
                    Todos: {importLogs.length}
                  </button>
                  <button
                    type="button"
                    onClick={() => setLogFilter('success')}
                    className={`px-3 py-1 rounded-lg text-[10px] font-black uppercase transition-all ${
                      logFilter === 'success'
                        ? 'bg-emerald-600 text-white'
                        : 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100'
                    }`}
                  >
                    Ok: {stats.success}
                  </button>
                  <button
                    type="button"
                    onClick={() => setLogFilter('error')}
                    className={`px-3 py-1 rounded-lg text-[10px] font-black uppercase transition-all ${
                      logFilter === 'error'
                        ? 'bg-rose-600 text-white'
                        : 'bg-rose-50 text-rose-700 hover:bg-rose-100'
                    }`}
                  >
                    Error: {stats.error}
                  </button>
                  <button
                    type="button"
                    onClick={() => setLogFilter('skipped')}
                    className={`px-3 py-1 rounded-lg text-[10px] font-black uppercase transition-all ${
                      logFilter === 'skipped'
                        ? 'bg-amber-500 text-white'
                        : 'bg-amber-50 text-amber-700 hover:bg-amber-100'
                    }`}
                  >
                    Omitida: {stats.skipped}
                  </button>
                </div>
              </div>

              {/* KPIs */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm">
                  <div className="text-[9px] font-black uppercase tracking-widest text-slate-400">Filas leidas</div>
                  <div className="mt-2 text-2xl font-black text-slate-800">{rawRows.length}</div>
                </div>
                <div className="bg-white p-4 rounded-2xl border border-emerald-100 shadow-sm">
                  <div className="text-[9px] font-black uppercase tracking-widest text-emerald-600">Precios validos</div>
                  <div className="mt-2 text-2xl font-black text-emerald-600">{stats.success}</div>
                </div>
                <div className="bg-white p-4 rounded-2xl border border-rose-100 shadow-sm">
                  <div className="text-[9px] font-black uppercase tracking-widest text-rose-600">Sin match</div>
                  <div className="mt-2 text-2xl font-black text-rose-600">{stats.error}</div>
                </div>
                {isCreateMode ? (
                  <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm">
                    <div className="text-[9px] font-black uppercase tracking-widest text-slate-400 flex items-center gap-1">
                      <EyeOff size={10} /> Quedarán ocultos
                    </div>
                    <div className="mt-2 text-2xl font-black text-slate-600">{hiddenProductCount}</div>
                  </div>
                ) : (
                  <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm">
                    <div className="text-[9px] font-black uppercase tracking-widest text-slate-400">
                      Productos impactados
                    </div>
                    <div className="mt-2 text-2xl font-black text-slate-800">{importedProductCount}</div>
                  </div>
                )}
              </div>

              {/* Info banner (create mode) */}
              {isCreateMode && hiddenProductCount > 0 && (
                <div className="mb-5 rounded-2xl border border-amber-100 bg-amber-50/70 px-4 py-3 flex items-start gap-3">
                  <EyeOff size={16} className="text-amber-500 mt-0.5 shrink-0" />
                  <p className="text-[11px] font-bold text-amber-700 leading-relaxed">
                    <span className="font-black">{hiddenProductCount} producto{hiddenProductCount !== 1 ? 's' : ''}</span> de tu catálogo no figuran en el archivo y quedarán <span className="font-black">ocultos</span> en esta lista. Podés cambiarlos después desde la edición de la lista.
                  </p>
                </div>
              )}

              {/* Logs list */}
              <div className="space-y-3 max-h-[300px] overflow-y-auto pr-1 mb-6">
                {filteredLogs.map((log) => {
                  const statusMeta = getStatusMeta(log.status);
                  return (
                    <div
                      key={`${log.row}-${log.status}-${log.productId || 'no-product'}`}
                      className={`rounded-2xl border p-4 ${statusMeta.card}`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex items-start gap-3 min-w-0">
                          <div className="mt-0.5">{statusMeta.icon}</div>
                          <div className="min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">
                                Fila {log.row}
                              </span>
                              <span className={`px-2 py-1 rounded-full text-[9px] font-black uppercase ${statusMeta.badge}`}>
                                {statusMeta.label}
                              </span>
                            </div>
                            <p className="mt-1 text-[11px] font-bold text-slate-700 leading-relaxed">
                              {log.msg}
                            </p>
                          </div>
                        </div>
                        {log.status === 'success' && log.price != null && (
                          <div className="text-right shrink-0">
                            <div className="text-[9px] font-black uppercase tracking-widest text-slate-400">
                              Precio
                            </div>
                            <div className="mt-1 text-[11px] font-black text-slate-800">
                              $ {Number(log.price || 0).toLocaleString('es-AR', { minimumFractionDigits: 2 })}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}

                {!filteredLogs.length && (
                  <div className="rounded-2xl border border-dashed border-slate-200 bg-white px-4 py-10 text-center text-[10px] font-black uppercase tracking-widest text-slate-300">
                    No hay filas para ese filtro
                  </div>
                )}
              </div>

              {/* Footer actions */}
              {isCreateMode ? (
                /* CREATE MODE: nombre de lista + botón crear */
                <div className="border-t border-slate-200 pt-5 space-y-4">
                  <div className="flex items-center gap-2 mb-1">
                    <Tag size={14} className="text-slate-400" />
                    <span className="text-[9px] font-black uppercase tracking-widest text-slate-500">
                      Nombre de la nueva lista
                    </span>
                  </div>
                  <input
                    type="text"
                    value={newListName}
                    onChange={(e) => setNewListName(e.target.value.toUpperCase())}
                    placeholder="EJ: MAYORISTA, CLIENTES VIP, PROMO JULIO..."
                    className="w-full bg-slate-50 border border-slate-200 px-4 py-3 rounded-2xl text-[11px] font-black uppercase outline-none focus:ring-2 focus:ring-[#51A7FD]/10 focus:border-[#51A7FD] transition-all shadow-inner"
                  />
                  <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
                    <button
                      type="button"
                      onClick={() => setStep(2)}
                      className="px-5 py-3 rounded-xl border border-slate-200 text-slate-600 font-bold text-xs uppercase hover:bg-slate-50 transition-colors"
                    >
                      Rehacer mapeo
                    </button>
                    <button
                      type="button"
                      onClick={handleCreateList}
                      disabled={creating || !stats.success || !newListName.trim()}
                      className="px-6 py-3 rounded-2xl bg-[#3FF2AA] text-[#0B2A36] font-black text-[10px] uppercase tracking-widest hover:brightness-95 transition-all shadow-lg disabled:opacity-50 flex items-center gap-2"
                    >
                      {creating ? (
                        <div className="animate-spin w-4 h-4 border-2 border-current border-t-transparent rounded-full" />
                      ) : (
                        <CheckCircle2 size={16} />
                      )}
                      {creating ? 'Creando lista...' : 'Crear nueva lista'}
                    </button>
                  </div>
                </div>
              ) : (
                /* APPLY MODE: comportamiento original */
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                  <div className="flex items-center gap-2 text-[11px] font-bold text-slate-500">
                    <AlertTriangle size={14} className="text-amber-500" />
                    Al aplicar, estos precios se cargan en el borrador actual. Despues guarda la lista para
                    persistirlos.
                  </div>

                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() => setStep(2)}
                      className="px-5 py-3 rounded-xl border border-slate-200 text-slate-600 font-bold text-xs uppercase hover:bg-slate-50 transition-colors"
                    >
                      Rehacer mapeo
                    </button>
                    <button
                      type="button"
                      onClick={handleApplyImport}
                      disabled={!stats.success}
                      className="px-6 py-3 rounded-2xl bg-[#3FF2AA] text-[#0B2A36] font-black text-[10px] uppercase tracking-widest hover:brightness-95 transition-all shadow-lg disabled:opacity-50"
                    >
                      Aplicar a esta lista
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {(loading || progress) && (
          <div className="border-t border-slate-200 bg-white px-5 py-3 text-[11px] font-bold text-slate-500">
            {progress || 'Procesando importacion...'}
          </div>
        )}
      </div>
    </div>
  );
}

export default PriceListImportModal;
